import { Children, cloneElement, useEffect, useState } from 'react';
import { DATE_YMD } from '@/configs/constants';
import { cn } from '@/utils/utils';
import { addHours, addMonths, format, isAfter } from 'date-fns';
import { MoveRight, X } from 'lucide-react';
import { isDateRange, NavProps } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import { Button, ButtonCus } from '../common/Button';
import {
    Popover,
    PopoverContent,
    PopoverTrigger
} from '../common/Popover';
import CustomTimePicker from './CustomTimePicker';
import { ReactDayPickerProps } from './ReactDayPicker';
import { CalenderPlusIcon } from '../icon';
import { ReactDayPickerRange } from './ReactDayPickerRange';

type CalendarProps = ReactDayPickerProps & {
    mode: 'single' | 'multiple' | 'range',
    selected: any,
    onSelect: any,
    placeholder?: string,
    calendarDisabled?: boolean,
    isClearRental?: boolean,
    setIsClearRental?: any,
    className?: string
};

export default function CalendarRentalPeriod({
    mode = 'range',
    selected,
    onSelect,
    placeholder = 'placeholder.select_date',
    calendarDisabled = false,
    isClearRental,
    setIsClearRental,
    className,
    ...props
}: CalendarProps) {
    // hooks
    const { t } = useTranslation();

    // state
    const [openCalendar, setOpenCalendar] = useState<boolean>(false);

    const [tempDateRange, setTempDateRange] = useState<any>(selected || {});
    const [tempRentalTime, setTempRentalTime] = useState<string>('00:00');
    const [tempReturnTime, setTempReturnTime] = useState<string>('23:00');

    // lifecycle
    useEffect(() => {
        if (!isClearRental) return;
        onSelect(undefined);
        setTempDateRange({});
        setTempRentalTime('00:00');
        setTempReturnTime('23:00');
    }, [isClearRental]);

    // Reset temp state when opening Popover
    useEffect(() => {
        if (openCalendar) {
            setTempDateRange(selected || {});
            setTempRentalTime(selected?.rentalTime || '00:00');
            setTempReturnTime(selected?.returnTime || '23:00');
        }
    }, [openCalendar, selected]);

    const CalendarChildren = () => {
        const limitMonth = addMonths(new Date(), 6);
        const [fromMonth, setFromMonth] = useState<Date>(
            tempDateRange?.from ?? tempDateRange?.to ?? new Date()
        );
        const [disableDates] = useState([
            { before: addHours(new Date(), 48) },
            { after: limitMonth }
        ]);
        const [earliestMonth] = useState(addMonths(new Date(), 0).getMonth());

        return (
            <ReactDayPickerRange
                // mode={mode}
                mode='range'
                className='max-w-full w-full xsm:!p-3 !p-3 shadow-none'
                showOutsideDays={false}
                selected={tempDateRange}
                onSelect={(date) => {
                    if (!date || !date?.from) {
                        setTempDateRange({});
                        return;
                    }
                    setTempDateRange({
                        from: date.from,
                        to: date.to
                    });
                    if (date.from) {
                        setFromMonth(date.from);
                    }
                }}
                month={fromMonth}
                onMonthChange={setFromMonth}
                modifiersClassNames={{
                    booked: 'day-booked',
                    blocked: 'day-blocked'
                }}
                components={{
                    Nav: ({ children, className, ...props }: NavProps) => {
                        const [previousButton, nextButton] = Children.map(children, child => child) as Array<React.ReactElement<any, string | React.JSXElementConstructor<any>>>;
                        const prevWithClass = cloneElement(previousButton, {
                            disabled: fromMonth.getMonth() <= earliestMonth && fromMonth.getFullYear() === (new Date()).getFullYear()
                        });
                        const nextWithClass = cloneElement(nextButton, {
                            disabled: isAfter(fromMonth, limitMonth) || (fromMonth.getFullYear() === limitMonth.getFullYear() && fromMonth.getMonth() >= limitMonth.getMonth())
                        });

                        return (
                            <nav className={cn('flex items-center min-h-8 justify-between', className)} {...props}>
                                {prevWithClass}
                                {nextWithClass}
                            </nav>
                        );
                    }
                }}
                disabled={disableDates}
                captionLayout='label'
            />
        );
    };

    const handleConfirm = () => {
        onSelect({
            from: tempDateRange?.from ?? new Date(),
            to: tempDateRange?.to ?? new Date(),
            rentalTime: tempRentalTime,
            returnTime: tempReturnTime
        });
        setOpenCalendar(false);
        setIsClearRental(false);
    };

    // Handle Popover close without confirm
    const handlePopoverClose = (open: boolean) => {
        setOpenCalendar(open);
        if (!open) {
            // Reset temp states when closing without confirm
            setTempDateRange(selected || {});
            setTempRentalTime('00:00');
            setTempReturnTime('23:00');
        }
    };

    return (
        <Popover open={openCalendar} onOpenChange={handlePopoverClose}>
            <PopoverTrigger asChild>
                <Button
                    disabled={calendarDisabled}
                    variant={selected ? 'filterActive' : 'filterDefault'}
                    size='filter'
                    className={cn(
                        'w-full flex items-center justify-start gap-[5px]',
                        className
                    )}
                >
                    <CalenderPlusIcon />
                    {selected && isDateRange(selected) && selected.from && (
                        <span className='pr-2'>
                            {
                                `${format(selected.from, DATE_YMD)} ${tempRentalTime} - ${selected.to ? format(selected.to, DATE_YMD) : ''} ${tempReturnTime}`
                            }
                        </span>
                    )}
                    {(!selected || (selected instanceof Array && selected.length === 0)) && (
                        <span>{t(`${placeholder}`)}</span>
                    )}
                    {selected && (
                        <X
                            strokeWidth={1}
                            className='h-4 w-4'
                            onClick={(e) => {
                                e.stopPropagation();
                                onSelect(undefined);
                                setTempDateRange({});
                                setTempRentalTime('00:00');
                                setTempReturnTime('23:00');
                            }}
                        />
                    )}

                </Button>
            </PopoverTrigger>
            <PopoverContent className='w-auto p-0 z-[1100] max-w-[430px] max-h-[580px]'>
                <CalendarChildren />
                <div className='flex my-4 space-x-2 justify-between items-center px-4'>
                    <div className='border rounded-md px-2 pt-2'>
                        <label className='text-[#4A4A4A] text-[13px]'>
                            {/* {t('words_title.rental_time')} */}
                            開始時刻：
                        </label>
                        <CustomTimePicker
                            className='w-[150px] text-[15px] border-none bg-white h-9 pl-0'
                            value={tempRentalTime}
                            onChange={newTime => setTempRentalTime(newTime)}
                            disableChangeMinute={true}
                        />
                    </div>
                    <div className='h-9 w-9 flex-shrink-0 rounded-full flex justify-center items-center border border-[#C1C1C1]'>
                        <MoveRight className='text-[#434242]' />
                    </div>
                    <div className='border rounded-md px-2 pt-2'>
                        <label className='text-[#4A4A4A] text-[13px]'>
                            {/* {t('words_title.return_time')} */}
                            返却時刻：
                        </label>
                        <CustomTimePicker
                            className='w-[150px] text-[15px] border-none bg-white h-9 pl-0'
                            value={tempReturnTime}
                            onChange={newTime => setTempReturnTime(newTime)}
                            disableChangeMinute={true}
                        />
                    </div>
                </div>
                <div className='flex justify-between items-center px-4 pb-4 gap-1.5'>
                    <div>
                        {
                            tempDateRange?.from && (
                                <span className='text-[15px] text-text'>
                                    {`${format(tempDateRange.from, DATE_YMD)} ${tempRentalTime} - ${tempDateRange.to ? format(tempDateRange.to, DATE_YMD) : ''} ${tempReturnTime}`}
                                </span>
                            )
                        }
                    </div>
                    <ButtonCus
                        disabled={!tempDateRange?.from}
                        variant='default'
                        size='fit'
                        className='px-6 h-11'
                        onClick={handleConfirm}
                    >
                        {t('button.apply')}
                    </ButtonCus>
                </div>
            </PopoverContent>
        </Popover>
    );
}
